import lscache from 'lscache';

import { isEmbeddedInFrame } from '../iframe';

export const CERNER_BCS_TOKEN_PARAM = 'bcs_token';
export const CERNER_PID_TYPE = 'cerner_federated_id';
export const CERNER_BCS_TOKEN_QUERY_PARAM = 'bcs_token';

// length of time in minutes to persist the BCS token to local storage
const BCS_TOKEN_EXPIRATION = 30;

// how long we wait to resolve the BCS token from HL SDK before we look elsewhere
export const BCS_TOKEN_TIMEOUT_MS = 5000;

// are we currently iframe'd inside of the cerner consumer framework?
export function isCCF(): boolean {
  return (
    typeof window !== 'undefined' &&
    isEmbeddedInFrame() &&
    Object.hasOwnProperty.call(window, '$HL')
  );
}

export function scrollToTopCCF() {
  // Scroll to the top of the page when PMC is embedded inside
  // the Cerner Healthelife patient portal.
  // This is necessary because the position of the modal is relative to the
  // viewport of PMC instead of the frame. As a result, if PMC is rendered
  // inside of a non-scrollable iframe so the user scrolls down in the outer
  // context, PMC will display the frame at the top of the page like it's
  // supposed to, except that will result in the modal appearing offscreen
  // because it's basing the position of the modal on the top of the PMC page
  // inside of the top of the outer context's viewport.
  // This uses the Cerner-specific scroll function because that's the only
  // way for code inside the frame to force the parent context to do something.
  // See https://stackoverflow.com/a/36412029
  if (isCCF() && `$HL` in window) {
    (window.$HL as any).App.scrollIntoView();
  }
}

export function getBcsTokenFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get(
    CERNER_BCS_TOKEN_QUERY_PARAM
  );
}

export function getBcsTokenFromLocalStorage(): string | null {
  return lscache.get(CERNER_BCS_TOKEN_PARAM);
}

export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message: string
): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function getBcsTokenFromHL(): Promise<string | null> {
  return withTimeout(
    Promise.resolve((window as any).$HL.App.getBCSToken()),
    BCS_TOKEN_TIMEOUT_MS,
    `Timed out after ${BCS_TOKEN_TIMEOUT_MS}ms waiting for token`
  );
}

/**
 * Get cerner "bcs_token" from the embedded healthelife library's $HL object,
 * falling back to the bcs_token URL param, then a previously cached token in
 * local storage, if $HL hangs or rejects.
 */
export async function getCernerEmbeddedBcsToken(): Promise<string | null> {
  if ('$HL' in window === false) {
    // we're trying to get ccf session data but the CCF js isn't loaded
    return null;
  }

  try {
    return await getBcsTokenFromHL();
  } catch (e) {
    const fallbackToken = getBcsTokenFromUrl() ?? getBcsTokenFromLocalStorage();
    if (fallbackToken) return fallbackToken;
    throw e;
  }
}

/**
 * Persists a bcs_token found on the URL into localStorage (via lscache) under
 * the same key. Needed because client-side route changes (e.g. to /book) drop
 * the query param, but the scheduling MFE reads this key via lscache too.
 */
export function persistBcsToken(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const bcsToken = getBcsTokenFromUrl();
  if (bcsToken) {
    lscache.set(CERNER_BCS_TOKEN_PARAM, bcsToken, BCS_TOKEN_EXPIRATION);
  }
}
